import { useState, useEffect } from 'react';
import { api } from '../services/api';

interface Client {
  id: string;
  name: string;
  description?: string;
  color?: string;
  icon?: string;
  notes?: string;
  default_permission?: 'allow_all' | 'deny_all';
  totalServers: number;
  totalQueries: number;
  totalExecutions: number;
  createdAt: string;
}

export const useClients = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchClients = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.clients.list();
      setClients(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch clients');
    } finally {
      setLoading(false);
    }
  };

  const createClient = async (name: string, description?: string) => {
    try {
      const newClient = await api.clients.create(name, description);
      setClients([...clients, newClient]);
      return newClient;
    } catch (err: any) {
      throw new Error(err.response?.data?.error || 'Failed to create client');
    }
  };

  const updateClient = async (id: string, data: {
    name?: string;
    description?: string;
    color?: string;
    icon?: string;
    notes?: string;
    default_permission?: 'allow_all' | 'deny_all';
  }) => {
    try {
      const updatedClient = await api.clients.update(id, data);
      setClients(clients.map(c => c.id === id ? updatedClient : c));
      return updatedClient;
    } catch (err: any) {
      throw new Error(err.response?.data?.error || 'Failed to update client');
    }
  };

  const deleteClient = async (id: string) => {
    try {
      await api.clients.delete(id);
      setClients(clients.filter(c => c.id !== id));
    } catch (err: any) {
      throw new Error(err.response?.data?.error || 'Failed to delete client');
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  return {
    clients,
    loading,
    error,
    fetchClients,
    createClient,
    updateClient,
    deleteClient
  };
};
